
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Search, X, ChevronDown, User, Bike, Phone, Hash, MapPin } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

export interface SearchItem {
  id: string;
  primaryText: string;
  secondaryText?: string;
  metadata?: Record<string, any>;
  type?: 'bike' | 'courier';
  status?: string;
  icon?: React.ReactNode;
}

interface AdvancedSearchSelectProps {
  items: SearchItem[];
  value?: string;
  onValueChange: (value: string, item?: SearchItem) => void;
  placeholder?: string;
  searchFields?: string[];
  groupBy?: (item: SearchItem) => string;
  renderItem?: (item: SearchItem, searchQuery: string) => React.ReactNode;
  className?: string;
  disabled?: boolean;
  maxHeight?: string;
}

export const AdvancedSearchSelect: React.FC<AdvancedSearchSelectProps> = ({
  items,
  value,
  onValueChange,
  placeholder = "Поиск...",
  searchFields = ['primaryText', 'secondaryText'],
  groupBy,
  renderItem,
  className = "",
  disabled = false,
  maxHeight = "300px"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Получаем выбранный элемент
  const selectedItem = items.find(item => item.id === value);
  
  // Фильтрация результатов с расширенным поиском
  const filteredItems = useMemo(() => {
    if (!debouncedSearchQuery) return items;
    
    const query = debouncedSearchQuery.toLowerCase().trim();
    
    return items.filter(item => {
      // Поиск по основным полям
      const mainFieldsMatch = searchFields.some(field => {
        const fieldValue = item[field as keyof SearchItem];
        return typeof fieldValue === 'string' && 
               fieldValue.toLowerCase().includes(query);
      });
      
      // Поиск по метаданным
      const metadataMatch = item.metadata ? 
        Object.values(item.metadata).some(value => 
          typeof value === 'string' && value.toLowerCase().includes(query)
        ) : false;
      
      return mainFieldsMatch || metadataMatch;
    });
  }, [items, debouncedSearchQuery, searchFields]);
  
  // Группировка результатов
  const groupedItems = useMemo(() => {
    if (!groupBy) return { 'Все результаты': filteredItems };
    
    const groups: Record<string, SearchItem[]> = {};
    filteredItems.forEach(item => {
      const group = groupBy(item);
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    
    return groups;
  }, [filteredItems, groupBy]);
  
  // Обработка выбора элемента
  const handleSelect = (item: SearchItem) => {
    onValueChange(item.id, item);
    setSearchQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };
  
  // Обработка клавиатуры
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      const totalItems = filteredItems.length;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % totalItems);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && filteredItems[selectedIndex]) {
            handleSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredItems]);
  
  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Функция подсветки совпадений
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <mark key={index} className="bg-yellow-200 px-1 rounded text-black">{part}</mark> : 
        part
    );
  };
  
  // Рендер элемента по умолчанию
  const defaultRenderItem = (item: SearchItem, searchQuery: string) => (
    <div className="flex items-center space-x-3 p-3">
      {item.icon || (item.type === 'bike' ? <Bike className="h-4 w-4" /> : <User className="h-4 w-4" />)}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">
          {highlightMatch(item.primaryText, searchQuery)}
        </div>
        {item.secondaryText && (
          <div className="text-xs text-muted-foreground">
            {highlightMatch(item.secondaryText, searchQuery)}
          </div>
        )}
        {item.metadata && (
          <div className="flex gap-1 mt-1">
            {Object.entries(item.metadata).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-xs">
                {key}: {highlightMatch(String(value), searchQuery)}
              </Badge>
            ))}
          </div>
        )}
      </div>
      {item.status && (
        <Badge variant={item.status === 'online' ? 'default' : 'secondary'}>
          {item.status}
        </Badge>
      )}
    </div>
  );
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Поле ввода */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={selectedItem ? selectedItem.primaryText : placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-10"
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedIndex(-1);
                inputRef.current?.focus();
              }}
              className="h-6 w-6 p-0 hover:bg-muted"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      
      {/* Выпадающий список результатов */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 shadow-lg border">
          <CardContent className="p-0">
            <ScrollArea className="w-full" style={{ maxHeight }}>
              {Object.keys(groupedItems).length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Ничего не найдено
                </div>
              ) : (
                Object.entries(groupedItems).map(([groupName, groupItems]) => (
                  <div key={groupName}>
                    {Object.keys(groupedItems).length > 1 && (
                      <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 border-b">
                        {groupName} ({groupItems.length})
                      </div>
                    )}
                    {groupItems.map((item, index) => {
                      const globalIndex = filteredItems.indexOf(item);
                      const isSelected = globalIndex === selectedIndex;
                      
                      return (
                        <div
                          key={item.id}
                          className={`cursor-pointer border-b border-border/40 last:border-b-0 transition-colors ${
                            isSelected ? 'bg-accent' : 'hover:bg-accent/50'
                          }`}
                          onClick={() => handleSelect(item)}
                        >
                          {renderItem ? 
                            renderItem(item, debouncedSearchQuery) : 
                            defaultRenderItem(item, debouncedSearchQuery)
                          }
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
      
      {/* Информация о выбранном элементе */}
      {selectedItem && !isOpen && (
        <div className="mt-2 text-xs text-muted-foreground">
          Выбрано: {selectedItem.primaryText}
          {selectedItem.secondaryText && ` (${selectedItem.secondaryText})`}
        </div>
      )}
    </div>
  );
};
